import { useMemo } from 'react';
import { CardItem, CardItemLabel, CardItemContent, CardItemValue } from '../ui/details-card';
import { MapPin, Phone, SquareCheck, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatPhoneNumber, getCodeLanguagesList } from '../../lib/utils';
import { Tooltip } from '@nextui-org/react';

function toWebsiteHref(value) {
  if (!value) return null;
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function getDirectionsUrl(data) {
  if (data?.latitude != null && data?.longitude != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${data.latitude},${data.longitude}`;
  }

  const query = [
    data?.providerAddress1,
    data?.providerAddress2,
    data?.providerCity,
    data?.providerState,
    data?.providerZip,
  ].filter(Boolean).join(', ');

  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

const fieldClass = 'pb-3 border-b border-slate-300';

const DetailField = ({ label, labelExtra = null, children }) => (
  <div className={fieldClass}>
    <CardItem>
      {label ? (
        <CardItemLabel>
          <div className='flex flex-row gap-2 items-center'>
            {label}
            {labelExtra}
          </div>
        </CardItemLabel>
      ) : null}
      <CardItemContent className='flex-col items-start gap-1'>
        {children}
      </CardItemContent>
    </CardItem>
  </div>
);

const DoctorDetails = props => {
  const { data = {} } = props;
  const { t } = useTranslation();
  const languages = getCodeLanguagesList(data?.languages);
  const locations = useMemo(() => {
    const list = Array.isArray(data?.locations) ? data.locations.slice() : [];
    return list.sort((a, b) => {
      const aHas = !!(a?.practitionerPCPID && String(a.practitionerPCPID).trim() !== '');
      const bHas = !!(b?.practitionerPCPID && String(b.practitionerPCPID).trim() !== '');
      if (aHas === bHas) return 0;
      return aHas ? -1 : 1;
    });
  }, [data?.locations]);
  const directionsUrl = getDirectionsUrl(data);
  const websiteHref = toWebsiteHref(data?.payeeWebsite);

  return (
    <div className='w-full flex flex-col justify-center items-center gap-10'>
        <div className='w-full grid grid-cols-12 gap-5 sm:gap-8'>
            <div className='col-span-12 sm:col-span-6 flex flex-col gap-5'>
                <DetailField label={t('Name')}>
                    <CardItemValue className='text-2xl'>{`${data?.practitionerFirstName} ${data?.practitionerLastName}, ${data?.practitionerTitle}`}</CardItemValue>
                </DetailField>
                <DetailField
                    label={t('Provider ID Number')}
                    labelExtra={(
                        <Tooltip placement='right-end' content={<div className='text-sm max-w-xs'>{t(`Providers in the same group may have different IDs based on their office location. Be sure to select the ID for the office you plan to visit.`)}</div>}>
                            <Info size={16} />
                        </Tooltip>
                    )}
                >
                    <CardItemValue>{data?.practitionerPCPID}</CardItemValue>
                </DetailField>
                <div className={fieldClass}>
                    <CardItem>
                        <CardItemContent className='flex-col items-start gap-1'>
                            <CardItemValue>{data?.providerAddress1}{data?.providerAddress2 ? `, ${data?.providerAddress2}` : ''}</CardItemValue>
                            <CardItemValue>{data?.providerCity}, {data?.providerState} {data?.providerZip}</CardItemValue>
                            <CardItemValue>
                                {directionsUrl ? (
                                    <a href={directionsUrl} target='_blank' rel='noreferrer' className='underline text-gray-800 hover:text-gray-800 font-normal'>{t('Get Directions')}</a>
                                ) : t('Get Directions')}
                            </CardItemValue>
                            <div className='flex flex-row gap-2 items-center'>
                                <Phone size={28} strokeWidth={1} className='text-primary'/>
                                <CardItemValue>{(data?.providerPhone ? <a href={`tel:${data.providerPhone}`} className='underline text-gray-800 hover:text-gray-800 font-normal'>{formatPhoneNumber(data?.providerPhone)}</a> : null)}</CardItemValue>
                            </div>
                            {websiteHref ? (
                                <CardItemValue>
                                    <a href={websiteHref} target='_blank' rel='noreferrer' className='underline text-gray-800 hover:text-gray-800 font-normal'>{data.payeeWebsite}</a>
                                </CardItemValue>
                            ) : null}
                        </CardItemContent>
                    </CardItem>
                </div>
                <DetailField label={t('Provider Office Hours')}>
                    <CardItemValue>{data?.providerOfficeHours}</CardItemValue>
                </DetailField>
                <DetailField label={t('After Hours/Weekend Availability')}>
                    <CardItemValue>{data?.providerAfterHoursWeekendAvailability}</CardItemValue>
                    <div className='text-sm text-gray-600'>{t('For emergencies, please call 911.')}</div>
                </DetailField>
                <DetailField label={t('Telehealth')}>
                    <CardItemValue>{data?.providerTelehealth}</CardItemValue>
                </DetailField>
                <DetailField label={t('Accessibility')}>
                    <CardItemValue>{data?.providerAccessibility}</CardItemValue>
                </DetailField>
            </div>
            <div className='col-span-12 sm:col-span-6 flex flex-col gap-5'>
                <DetailField label={t('Specialty')}>
                    <CardItemValue>{data?.specialties?.length > 0 ? (
                        <ul className='flex flex-col gap-1'>
                            {data.specialties.map((item) => <li key={item}>{item}</li>)}
                        </ul>
                    ) : null}</CardItemValue>
                </DetailField>
                <div className={fieldClass}>
                    <CardItem>
                        <CardItemLabel>{t('About the Provider')}</CardItemLabel>
                    </CardItem>
                </div>
                <DetailField label={t('Gender')}>
                    <CardItemValue>
                        <div className='text-sm text-primary font-semibold bg-orange-100 px-4 py-1 rounded-full'>{data?.gender === 'M' ? 'MALE' : 'FEMALE'}</div>
                    </CardItemValue>
                </DetailField>
                <DetailField label={t('Languages')}>
                    <CardItemValue>{languages.map(item => <div key={item} className='inline-block m-1 text-sm text-secondary font-semibold bg-gray-200 px-4 py-1 rounded-full'>{item}</div>)}</CardItemValue>
                </DetailField>
                <DetailField label={t('Group Affiliation')}>
                    <CardItemValue>{data?.payees?.length > 0 ? (
                        <ul className='flex flex-col gap-1'>
                            {data.payees.map((item) => <li key={item}>{item}</li>)}
                        </ul>
                    ) : null}</CardItemValue>
                </DetailField>
                <DetailField label={t('Hospitals Affiliations')}>
                    <CardItemValue>{data?.hospitalPrivileges?.length > 0 ? (
                        <ul className='flex flex-col gap-1'>
                            {data.hospitalPrivileges.map((item) => <li key={item.name}>{item.name}</li>)}
                        </ul>
                    ) : null}</CardItemValue>
                </DetailField>
                <DetailField label={t('Accepting New Patients?')}>
                    <CardItemValue>{data?.providerAcceptsNewPatient === 'Y' ? <SquareCheck size={28} strokeWidth={1} className='text-green-600' /> : t('No')}</CardItemValue>
                </DetailField>
                <DetailField label={t('Board Certification(s)')}>
                    <CardItemValue>{data?.boardCertifications?.length > 0 ? (
                        <ul className='flex flex-col gap-1'>
                            {data.boardCertifications.map((item) => <li key={item.number || item.name}>{item.name}</li>)}
                        </ul>
                    ) : 'N/A'}</CardItemValue>
                </DetailField>
                <DetailField label={t('Cultural Competency?')}>
                    <CardItemValue>{data?.providerCulturalCompetency}</CardItemValue>
                </DetailField>
            </div>
        </div>
        <div className='w-full flex flex-row gap-5'>
            <CardItem className='w-full'>
                <CardItemLabel className='w-full text-start py-5'>{t('Other Locations')}</CardItemLabel>
                <CardItemContent className='w-full'>
                    <CardItemValue className='w-full'>
                        <ul className='flex flex-col gap-2'>
                            {locations.map((item) => 
                            <li key={item?.practitionerPCPID || `${item?.address1}-${item?.zip5}`} className='grid grid-cols-12 border-b border-slate-300 pb-3 gap-3'>
                                <div className='col-span-12 sm:col-span-4 flex flex-row justify-start items-center gap-3'>{item?.practitionerPCPID || ''}</div>
                                <div className='col-span-12 sm:col-span-4 flex flex-row justify-start items-center gap-3'>
                                    <MapPin size={28} strokeWidth={1} className='text-primary'/> 
                                    <div>
                                        <div>{item.address1}{item.address2 ? `, ${item.address2}` : ''}</div>
                                        <div>{item.city}{item.state || item.zip5 ? `, ` : ''}{item.state ? `${item.state}` : ''}{item.zip5 ? ` ${item.zip5}` : ''}</div>
                                    </div>
                                </div>
                                <div className='col-span-12 sm:col-span-4 flex flex-row justify-start items-center gap-3'>
                                    <Phone size={28} strokeWidth={1} className='text-primary'/> {(item?.phone ? <a href={`tel:${item.phone}`} className='underline text-gray-800 hover:text-gray-800 font-normal'>{formatPhoneNumber(item.phone)}</a> : null)}
                                </div>
                            </li>)}
                        </ul>
                    </CardItemValue>
                </CardItemContent>
            </CardItem>
        </div>
    </div>
    
  );
};

export default DoctorDetails;
